import { useTranslations } from '@vocab/react';
import { Strong, Text, TextLink, Stack } from 'braid-design-system';
import React from 'react';

import {
  CONNECTION_BUSINESS_ADDRESS_ERROR,
  CONNECTION_SAVE_DRAFT_ERROR,
  CONNECTION_SESSION_EXPIRED_ERROR,
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_GENERIC_ERROR,
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_INVOICE_ORDER_NOT_FOUND_ERROR,
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_ERROR,
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_EXPIRED_ERROR,
  EDIT_PAGE_CONNECTION_JOB_EDIT_ERROR,
  EDIT_PAGE_CONNECTION_JOB_EDIT_NO_REVISION_ID_ERROR,
  EDIT_PAGE_CONNECTION_ORDER_CREATE_GENERIC_ERROR,
  EDIT_PAGE_CONNECTION_ORDER_CREATE_NO_JOB_ID,
  EDIT_PAGE_CONNECTION_ORDER_CREATE_NO_ORDER_ID_ERROR,
  EDIT_PAGE_CONNECTION_SAVE_DRAFT_ERROR,
} from 'src/app/constants/errorMessages';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';

import translations from './.vocab';

export interface Props {
  errorType?: string;
}

const ConnectionError = ({ errorType }: Props) => {
  const { t } = useTranslations(translations);
  const { createContactUsUrl } = useLocalisedRoutes();

  let bodyText;
  let errorTitle = t('connection-error-default-title');
  let omitContactCustomerService = false;
  let nextSteps;

  const RefreshThePage = () => <>{t('connection-error-reload-the-page')}</>;

  switch (errorType) {
    case CONNECTION_SAVE_DRAFT_ERROR:
      errorTitle = t('connection-error-save-draft-title');
      bodyText = t('connection-error-save-draft-text');
      nextSteps = t('connection-error-save-draft-next-steps');
      break;

    case CONNECTION_BUSINESS_ADDRESS_ERROR:
      errorTitle = t('connection-error-business-address-title');
      bodyText = t('connection-error-business-address-text');
      nextSteps = <RefreshThePage />;
      break;

    case CONNECTION_SESSION_EXPIRED_ERROR:
    case EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_EXPIRED_ERROR:
      errorTitle = t('connection-error-session-expired-title');
      omitContactCustomerService = true;
      bodyText = <RefreshThePage />;
      break;

    case EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_GENERIC_ERROR:
    case EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_INVOICE_ORDER_NOT_FOUND_ERROR:
    case EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_ERROR:
    case EDIT_PAGE_CONNECTION_JOB_EDIT_ERROR:
    case EDIT_PAGE_CONNECTION_JOB_EDIT_NO_REVISION_ID_ERROR:
    case EDIT_PAGE_CONNECTION_ORDER_CREATE_GENERIC_ERROR:
    case EDIT_PAGE_CONNECTION_ORDER_CREATE_NO_JOB_ID:
    case EDIT_PAGE_CONNECTION_ORDER_CREATE_NO_ORDER_ID_ERROR:
    case EDIT_PAGE_CONNECTION_SAVE_DRAFT_ERROR:
      errorTitle = t('connection-error-publish-changes-title');
      bodyText = t('connection-error-publish-changes-text');
      omitContactCustomerService = true;
      break;

    default:
      bodyText = t('connection-error-default-text');
      nextSteps = t('connection-error-default-next-steps');
      break;
  }

  const contactCustomerService = (
    <>
      {' '}
      {t('connection-error-contact-cs', {
        CustomerServiceLink: (children) => (
          <TextLink href={createContactUsUrl()} target="_blank">
            {children}
          </TextLink>
        ),
      })}
    </>
  );

  return (
    <Stack space="small">
      <Text>
        <Strong>{errorTitle}</Strong>
      </Text>
      <Text>{bodyText}</Text>
      <Text>
        {nextSteps}
        {!omitContactCustomerService && contactCustomerService}
      </Text>
    </Stack>
  );
};

export default ConnectionError;
